import * as React from "react"
import { Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"

// The design system's page header (Page headers → "Basic — with right-side
// actions") for the admin screens, with an optional way back above the title
// and a row for badges under it.
export function AdminPageHeader({
  title,
  description,
  actions,
  back,
  meta,
}: {
  title: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
  back?: { href: string; label: string }
  meta?: React.ReactNode
}) {
  return (
    <div className="border-b border-hairline pb-6">
      {back && (
        <Link
          href={back.href}
          className="-ml-2 mb-2 inline-flex min-h-11 items-center gap-1.5 rounded-md px-2 text-sm text-ink-muted no-underline hover:text-ink-display"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {back.label}
        </Link>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="break-words">{title}</h1>
          {description && <p className="mt-1">{description}</p>}
          {meta && <div className="mt-3 flex flex-wrap items-center gap-2">{meta}</div>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  )
}

/** A titled block on an admin page: an h2 with an optional right-side action. */
export function AdminSection({
  id,
  title,
  description,
  action,
  children,
  className,
}: {
  id: string
  title: React.ReactNode
  description?: React.ReactNode
  action?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <section aria-labelledby={id} className={className ?? "mt-10"}>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h2 id={id}>{title}</h2>
          {description && <p className="mt-1">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

/** The zero-data panel (design system: Callout, "Empty states"). */
export function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="callout">
      <p>{children}</p>
    </div>
  )
}
